import React, { useState } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { FileText, X } from 'lucide-react';

function TaxInvoiceRequest({ order, userInfo, onClose, onSuccess }) {
    const [bizNumber, setBizNumber] = useState(userInfo?.businessNumber || '');
    const [companyName, setCompanyName] = useState(userInfo?.businessName || '');
    const [ceo, setCeo] = useState(userInfo?.representativeName || '');
    const [bizType, setBizType] = useState('');
    const [email, setEmail] = useState(userInfo?.email || '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!bizNumber.trim()) {
            setError('사업자등록번호를 입력해주세요.');
            return;
        }
        if (!companyName.trim()) {
            setError('상호를 입력해주세요.');
            return;
        }
        if (!ceo.trim()) {
            setError('대표자명을 입력해주세요.');
            return;
        }
        if (!email.trim()) {
            setError('세금계산서 수신 이메일을 입력해주세요.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            await addDoc(collection(db, 'taxInvoiceRequests'), {
                orderId: order.id,
                buyerId: userInfo.uid,
                sellerId: order.sellerId,
                businessInfo: {
                    bizNumber: bizNumber.trim(),
                    companyName: companyName.trim(),
                    ceo: ceo.trim(),
                    bizType: bizType.trim(),
                    email: email.trim()
                },
                status: 'requested',
                requestedAt: serverTimestamp()
            });

            if (onSuccess) onSuccess();
            if (onClose) onClose();
        } catch (err) {
            console.error('세금계산서 요청 실패:', err);
            setError('요청에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <FileText size={24} color="#6366f1" />
                        <h2 style={styles.headerTitle}>세금계산서 요청</h2>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {error && (
                        <div style={styles.errorBox}>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* 사업자등록번호 */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>사업자등록번호</label>
                        <input
                            type="text"
                            value={bizNumber}
                            onChange={(e) => setBizNumber(e.target.value)}
                            placeholder="000-00-00000"
                            style={styles.input}
                        />
                    </div>

                    {/* 상호 */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>상호</label>
                        <input
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            placeholder="상호명을 입력해주세요"
                            style={styles.input}
                        />
                    </div>

                    {/* 대표자명 */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>대표자명</label>
                        <input
                            type="text"
                            value={ceo}
                            onChange={(e) => setCeo(e.target.value)}
                            placeholder="대표자명을 입력해주세요"
                            style={styles.input}
                        />
                    </div>

                    {/* 업태 */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>업태</label>
                        <input
                            type="text"
                            value={bizType}
                            onChange={(e) => setBizType(e.target.value)}
                            placeholder="업태를 입력해주세요"
                            style={styles.input}
                        />
                    </div>

                    {/* 세금계산서 수신 이메일 */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>세금계산서 수신 이메일</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="example@email.com"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button style={styles.cancelBtn} onClick={onClose}>
          ,               취소
                    </button>
                    <button
                        style={{
                            ...styles.submitBtn,
                            ...(submitting ? styles.submitBtnDisabled : {})
                        }}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? '요청 중...' : '세금계산서 요청'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000
    },
    modal: {
        width: '100%',
        maxWidth: '520px',
        maxHeight: '90vh',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    headerTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a'
    },
    closeBtn: {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '24px'
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#ef4444',
        marginBottom: '20px'
    },
    fieldGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s'
    },
    footer: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        borderTop: '1px solid #e2e8f0'
    },
    cancelBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    submitBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s'
    },
    submitBtnDisabled: {
        backgroundColor: '#a5b4fc',
        cursor: 'not-allowed'
    }
};

export default TaxInvoiceRequest;
